
import { useState, useEffect, useMemo, useCallback, useRef, startTransition } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card"
import { Button } from "../components/ui/button"
import { Input } from "../components/ui/input"
import { Label } from "../components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select"
import { Badge } from "../components/ui/badge"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog"
import { Textarea } from "../components/ui/textarea"
import { Switch } from "../components/ui/switch"
import { Checkbox } from "../components/ui/checkbox"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "../components/ui/dropdown-menu"
import { Alert, AlertDescription } from "../components/ui/alert"
import { Separator } from "../components/ui/separator"
import { MoonLoader } from "../components/ui/moon-loader"
import { 
  Plus, 
  Edit, 
  Trash2, 
  ChevronDown, 
  ChevronRight,
  AlertCircle,
  CheckCircle,
  XCircle,
  ChevronLeft,
  
  ChevronsLeft,
  ChevronsRight,
  Search,
  Download,
  Upload,
  MoreHorizontal,
  Eye,
  Copy,
  RefreshCw,
  Building2,
  Layers,
  FolderOpen,
  Clock,
  Zap,
  BarChart3,
  Settings,
  FileText,
  Filter,
  
  Calculator
} from "lucide-react"
import { accountingApi, type AccountType, type Account, type AccountSummary } from "../lib/api/accounting"
import { useAuth } from "../contexts/auth-context"
import { formatApiError } from "../lib/error-utils"
import { useToast } from "../hooks/use-toast"
import { TranslatedText } from "../components/translated-text"
import { useTranslation } from "../hooks/useTranslation"

// Standard account types - only these 5 are valid
const STANDARD_ACCOUNT_TYPES = [
  { code: 'ASSET', name: 'Asset' },
  { code: 'LIABILITY', name: 'Liability' },
  { code: 'EQUITY', name: 'Equity' },
  { code: 'REVENUE', name: 'Revenue' },
  { code: 'EXPENSE', name: 'Expense' }
] as const

type StandardAccountTypeCode = typeof STANDARD_ACCOUNT_TYPES[number]['code']

interface AccountTreeNode extends Account {
  children: AccountTreeNode[]
  level: number
  isExpanded?: boolean
}

interface PaginationInfo {
  page: number
  pageSize: number
  totalCount: number
  totalPages: number
  hasNext: boolean
  hasPrev: boolean
}

interface AccountsWithPagination {
  accounts: Account[]
  pagination: PaginationInfo
}

interface BulkAction {
  id: string
  label: string
  icon: React.ReactNode
  action: (selectedIds: string[]) => Promise<void>
  destructive?: boolean
}

export function ChartOfAccounts() {
  
  const { isAuthenticated, isLoading: authLoading } = useAuth()
  const { toast } = useToast()
  const { tSync } = useTranslation()
  const [accountTypes, setAccountTypes] = useState<AccountType[]>([])
  const [accounts, setAccounts] = useState<Account[]>([])
  const [accountsWithPagination, setAccountsWithPagination] = useState<AccountsWithPagination | null>(null)
  const [accountTree, setAccountTree] = useState<AccountTreeNode[]>([])
  const [summary, setSummary] = useState<AccountSummary | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  
  // Search and filtering
  const [searchTerm, setSearchTerm] = useState("")
  const [filterType, setFilterType] = useState<string>("all")
  const [filterStatus, setFilterStatus] = useState<string>("all")
  const [showInactive, setShowInactive] = useState(false)
  
  // Selection and bulk operations
  const [selectedAccounts, setSelectedAccounts] = useState<Set<string>>(new Set())
  const [showBulkActions, setShowBulkActions] = useState(false)
  const [bulkOperationError, setBulkOperationError] = useState<string | null>(null)
  
  // Form states
  const [showAccountDialog, setShowAccountDialog] = useState(false)
  const [showTypeDialog, setShowTypeDialog] = useState(false)
  const [showImportDialog, setShowImportDialog] = useState(false)
  const [editingAccount, setEditingAccount] = useState<Account | null>(null)
  const [editingType, setEditingType] = useState<AccountType | null>(null)
  
  // View options
  const [viewMode, setViewMode] = useState<"list" | "tree" | "grid">("list")
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set())
  const expandedNodesRef = useRef<Set<string>>(expandedNodes)
  const [showAccountCodes, setShowAccountCodes] = useState(true)
  const [autoExpandTree, setAutoExpandTree] = useState(false)
  
  // Keep ref in sync with state
  useEffect(() => {
    expandedNodesRef.current = expandedNodes
  }, [expandedNodes])
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  
  // Form data
  const [accountForm, setAccountForm] = useState({
    name: "",
    code: "",
    description: "",
    accountTypeId: undefined as string | undefined,
    parentId: undefined as string | undefined,
    isActive: true
  })
  const [accountFormError, setAccountFormError] = useState<string | null>(null)
  const [accountFormLoading, setAccountFormLoading] = useState(false)
  
  const [typeForm, setTypeForm] = useState({
    code: "",
    name: "",
    description: "",
    normalBalance: "debit" as "debit" | "credit",
    category: ""
  })

  // Ensure accountTypeId is set when dialog opens for new account (only for valid standard types)
  // Use a ref to prevent infinite loops
  const hasSetDefaultTypeRef = useRef(false);
  const lastDialogStateRef = useRef({ open: false, editing: false });
  
  useEffect(() => {
    const currentState = { open: showAccountDialog, editing: !!editingAccount };
    const stateChanged = 
      currentState.open !== lastDialogStateRef.current.open ||
      currentState.editing !== lastDialogStateRef.current.editing;
    
    lastDialogStateRef.current = currentState;
    
    // Only process if dialog just opened (not on every render)
    if (showAccountDialog && !editingAccount && accountTypes.length > 0 && !accountForm.accountTypeId && stateChanged) {
      // Find the first valid standard account type
      const validAccountTypes = accountTypes.filter(type => {
        if (!type.id || !type.code) return false;
        const typeCode = type.code.toUpperCase().trim();
        return STANDARD_ACCOUNT_TYPES.some(std => std.code === typeCode);
      });
      
      if (validAccountTypes.length > 0 && !hasSetDefaultTypeRef.current) {
        hasSetDefaultTypeRef.current = true;
        setAccountForm((prev) => ({ ...prev, accountTypeId: validAccountTypes[0].id }));
      }
    } else if (!showAccountDialog) {
      // Reset the flag when dialog closes
      hasSetDefaultTypeRef.current = false;
    }
  }, [showAccountDialog, editingAccount, accountTypes, accountForm.accountTypeId]);

  // Helper: build account tree from flat list
  const buildAccountTree = useCallback((accounts: Account[], currentExpandedNodes: Set<string>): AccountTreeNode[] => {
    const accountMap = new Map<string, AccountTreeNode>()
    const rootAccounts: AccountTreeNode[] = []
    
    // Create nodes
    accounts.forEach(account => {
      accountMap.set(account.id, {
        ...account,
        children: [],
        level: 0,
        isExpanded: currentExpandedNodes.has(account.id)
      })
    })
    
    // Build hierarchy
    accounts.forEach(account => {
      const node = accountMap.get(account.id)
      if (!node) return
      
      if (account.parentId && accountMap.has(account.parentId)) {
        const parent = accountMap.get(account.parentId)!
        parent.children.push(node)
        node.level = parent.level + 1
      } else {
        rootAccounts.push(node)
      }
    })
    
    return rootAccounts
  }, [])

  // Flag to prevent auto-load useEffect from running during manual loads
  // Define this BEFORE loadData so loadData can access it
  const isManualLoadRef = useRef(false)
  const isLoadingRef = useRef(false) // Prevent concurrent loadData calls
  // Track when manual load completed to prevent auto-expand for a period
  const manualLoadCompletedAtRef = useRef<number | null>(null)

  // Load data (wrapped with useCallback to keep identity stable)
  const loadData = useCallback(async () => {
    // Prevent concurrent calls
    if (isLoadingRef.current) {
      return
    }
    
    isLoadingRef.current = true
    
    try {
      setLoading(true)
      setError(null)
      
      // Get company ID from localStorage
      const companyId = localStorage.getItem('company_id') || localStorage.getItem('companyId') || localStorage.getItem('company') || undefined
      
      // Get current showInactive value to avoid stale closure
      const currentShowInactive = showInactive
      
      // Load live data using the shared accounting API
      const [typesData, accountsResp] = await Promise.all([
        accountingApi.accountTypesApi.getAll(companyId),
        accountingApi.chartOfAccountsApi.getAll(companyId, currentShowInactive)
      ])

      setAccountTypes(Array.isArray(typesData) ? typesData : [])

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const accountsList: Account[] = Array.isArray((accountsResp as any))
        ? (accountsResp as Account[])
        : (accountsResp as unknown as { accounts: Account[]; pagination?: any })?.accounts ?? []

      setAccountsWithPagination(null)
      setAccounts(accountsList)

      // Use startTransition for non-urgent updates to keep UI responsive
      // Build tree outside transition to avoid blocking
      const tree = buildAccountTree(accountsList, expandedNodesRef.current)
      
      // For manual loads, delay tree update significantly to prevent blocking UI
      if (isManualLoadRef.current) {
        // Update summary immediately (lightweight)
        setSummary({
          totalAccounts: accountsList.length,
          activeAccounts: accountsList.filter(a => (a as any).isActive !== false).length,
          totalAccountTypes: Array.isArray(typesData) ? typesData.length : 0,
          maxDepth: 3,
          lastUpdated: new Date().toLocaleDateString()
        })
        
        // Delay tree update to prevent blocking - only if actually needed
        // Use a longer delay to ensure UI is completely responsive before updating tree
        // The auto-expand useEffect will be prevented by the cooldown period
        setTimeout(() => {
          // Double-check flag is still set (might have been reset)
          // Note: Even if flag is reset, we still update the tree - the cooldown will prevent auto-expand
          // Use startTransition to mark as non-urgent
          startTransition(() => {
            setAccountTree(tree)
          })
        }, 1500) // Longer delay to ensure UI is completely unblocked
      } else {
        // For auto-loads, use immediate transition
        startTransition(() => {
          setSummary({
            totalAccounts: accountsList.length,
            activeAccounts: accountsList.filter(a => (a as any).isActive !== false).length,
            totalAccountTypes: Array.isArray(typesData) ? typesData.length : 0,
            maxDepth: 3,
            lastUpdated: new Date().toLocaleDateString()
          })
          
          setAccountTree(tree)
        })
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load data")
    } finally {
      setLoading(false)
      isLoadingRef.current = false // Reset loading flag
    }
  }, [showInactive, buildAccountTree])

  // Auto-expand tree when switching to tree view and auto-expand is enabled
  // Use a ref to track if we've already processed this accountTree to avoid loops
  const processedTreeRef = useRef<string>('')
  const expandTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const lastTreeHashRef = useRef<string>('')
  
  useEffect(() => {
    // Early exit: Skip entirely if manual load or not in tree view
    // Check these FIRST before doing any work
    if (isManualLoadRef.current) {
      return
    }
    
    // Skip auto-expand if manual load completed recently (within last 3 seconds)
    // This prevents auto-expand from running immediately after manual load completes
    // Check this BEFORE any other work to avoid unnecessary computations
    if (manualLoadCompletedAtRef.current !== null) {
      const timeSinceManualLoad = Date.now() - manualLoadCompletedAtRef.current
      if (timeSinceManualLoad < 3000) {
        return
      }
      // Clear the ref after cooldown period
      manualLoadCompletedAtRef.current = null
    }
    
    if (viewMode !== "tree") {
      // Don't process if not in tree view - but still clear timeout
      if (expandTimeoutRef.current) {
        clearTimeout(expandTimeoutRef.current)
        expandTimeoutRef.current = null
      }
      processedTreeRef.current = ''
      return
    }
    
    // Create hash early to check if tree actually changed
    const treeHash = accountTree.map(n => n.id).join(',')
    
    // Skip if tree hasn't actually changed
    if (lastTreeHashRef.current === treeHash) {
      return
    }
    
    lastTreeHashRef.current = treeHash
    
    // Clear any pending timeout
    if (expandTimeoutRef.current) {
      clearTimeout(expandTimeoutRef.current)
    }
    
    // Only process if auto-expand is enabled
    if (!autoExpandTree) {
      return
    }
    
    // Debounce the expansion to prevent blocking UI
    expandTimeoutRef.current = setTimeout(() => {
      // Double-check manual load flag inside timeout
      if (isManualLoadRef.current) {
        return
      }
      
      // Double-check cooldown period inside timeout
      if (manualLoadCompletedAtRef.current !== null) {
        const timeSinceManualLoad = Date.now() - manualLoadCompletedAtRef.current
        if (timeSinceManualLoad < 3000) {
          return
        }
      }
      
      // Only process if tree actually changed or view settings changed
      if (processedTreeRef.current === treeHash && viewMode === "tree" && autoExpandTree) {
        return // Already processed this tree state
      }
      
      if (viewMode === "tree" && autoExpandTree && accountTree.length > 0) {
        const allNodeIds = new Set<string>()
        const collectNodeIds = (nodes: AccountTreeNode[]) => {
          nodes.forEach(node => {
            allNodeIds.add(node.id)
            if (node.children.length > 0) {
              collectNodeIds(node.children)
            }
          })
        }
        collectNodeIds(accountTree)
        
        // Use startTransition for non-urgent update
        startTransition(() => {
          setExpandedNodes(allNodeIds)
        })
        processedTreeRef.current = treeHash
      }
    }, 50) // Small debounce to batch updates
    
    return () => {
      if (expandTimeoutRef.current) {
        clearTimeout(expandTimeoutRef.current)
        expandTimeoutRef.current = null
      }
    }
  }, [viewMode, autoExpandTree, accountTree])

  // Bulk actions configuration
  const bulkActions: BulkAction[] = [
    {
      id: "activate",
      label: "Activate",
      icon: <CheckCircle className="h-4 w-4" />,
      action: handleBulkActivate
    },
    {
      id: "deactivate",
      label: "Deactivate",
      icon: <XCircle className="h-4 w-4" />,
      action: handleBulkDeactivate
    },
    {
      id: "export",
      label: "Export Selected",
      icon: <Download className="h-4 w-4" />,
      action: handleBulkExport
    },
    {
      id: "delete",
      label: "Delete",
      icon: <Trash2 className="h-4 w-4" />,
      action: handleBulkDelete,
      destructive: true
    }
  ]

  // Create a stable reference to loadData for manual calls
  const loadDataRef = useRef(loadData)
  useEffect(() => {
    loadDataRef.current = loadData
  }, [loadData])
  
  // Wrapper function for manual loads that prevents infinite loops
  const manualLoadData = useCallback(async () => {
    isManualLoadRef.current = true
    
    try {
      await loadDataRef.current()
    } catch (error) {
      throw error
    } finally {
      // Reset flag after a longer delay to allow all state updates and transitions to settle
      // This ensures the delayed tree update can still check the flag
      // Updated to 1600ms to match the new 1500ms delayed tree update + buffer
      setTimeout(() => {
        isManualLoadRef.current = false
        manualLoadCompletedAtRef.current = Date.now()
      }, 1600) // Reset after delayed tree update completes (1500ms + buffer)
    }
  }, [])
  
  // Track last showInactive value and initial mount to prevent duplicate calls
  const lastShowInactiveRef = useRef<boolean | null>(null) // null = not initialized
  const hasInitialLoadRef = useRef(false)
  
  // Load data on mount and when showInactive changes
  // Use loadDataRef to avoid dependency issues
  useEffect(() => {
    const isInitialMount = lastShowInactiveRef.current === null
    
    // Skip if this is a manual load
    if (isManualLoadRef.current) {
      // Still update the ref to track state
      if (isInitialMount) {
        lastShowInactiveRef.current = showInactive
      }
      return
    }
    
    // Skip if already loading
    if (isLoadingRef.current) {
      // Still update the ref to track state
      if (isInitialMount) {
        lastShowInactiveRef.current = showInactive
      }
      return
    }
    
    // On initial mount, always load (but only once)
    if (isInitialMount) {
      if (hasInitialLoadRef.current) {
        lastShowInactiveRef.current = showInactive
        return
      }
      hasInitialLoadRef.current = true
      lastShowInactiveRef.current = showInactive
      loadDataRef.current()
      return
    }
    
    // Skip if showInactive hasn't actually changed
    if (lastShowInactiveRef.current === showInactive) {
      return
    }
    
    lastShowInactiveRef.current = showInactive
    loadDataRef.current()
  }, [showInactive]) // Only reload when showInactive changes

  // Filter accounts based on search and filters
  // Memoize with stable dependencies to prevent unnecessary recalculations
  const filteredAccounts = useMemo(() => {
    if (!accounts || accounts.length === 0) {
      return []
    }
    
    const result = accounts.filter(account => {
      // Search filter
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase()
        const matchesSearch = 
          account.name.toLowerCase().includes(searchLower) ||
          account.code.toLowerCase().includes(searchLower) ||
          account.description?.toLowerCase().includes(searchLower) ||
          (account.accountType && account.accountType.toLowerCase().includes(searchLower))
        
        if (!matchesSearch) return false
      }
      
      // Type filter
      if (filterType !== "all" && account.accountTypeId !== filterType) {
        return false
      }
      
      // Status filter
      if (filterStatus === "active" && !account.isActive) return false
      if (filterStatus === "inactive" && account.isActive) return false
      
      return true
    })
    
    return result
  }, [accounts, searchTerm, filterType, filterStatus])
  

  // Bulk action handlers
  async function handleBulkActivate(selectedIds: string[]) {
    try {
      setBulkOperationError(null)
      await Promise.all(
        selectedIds.map(id => accountingApi.chartOfAccountsApi.update(id, { isActive: true }))
      )
      await manualLoadData()
      setSelectedAccounts(new Set())
      setShowBulkActions(false)
    } catch (error) {
      const msg = error instanceof Error ? error.message : "Failed to activate accounts"
      setBulkOperationError(msg)
    }
  }

  async function handleBulkDeactivate(selectedIds: string[]) {
    try {
      setBulkOperationError(null)
      await Promise.all(
        selectedIds.map(id => accountingApi.chartOfAccountsApi.update(id, { isActive: false }))
      )
      await manualLoadData()
      setSelectedAccounts(new Set())
      setShowBulkActions(false)
    } catch (error) {
      const msg = error instanceof Error ? error.message : "Failed to deactivate accounts"
      setBulkOperationError(msg)
    }
  }

  async function handleBulkExport(selectedIds: string[]) {
    try {
      setBulkOperationError(null)
      const selectedAccountsData = accounts.filter(account => selectedIds.includes(account.id))
      const csvContent = generateCSV(selectedAccountsData)
      downloadCSV(csvContent, "chart-of-accounts.csv")
      setSelectedAccounts(new Set())
      setShowBulkActions(false)
    } catch (error) {
      const msg = error instanceof Error ? error.message : "Failed to export accounts"
      setBulkOperationError(msg)
    }
  }

  async function handleBulkDelete(selectedIds: string[]) {
    if (!confirm(`Are you sure you want to delete ${selectedIds.length} accounts? This action cannot be undone.`)) {
      return
    }
    
    try {
      setBulkOperationError(null)
  await Promise.all(selectedIds.map(id => accountingApi.chartOfAccountsApi.delete(id)))
      await manualLoadData()
      setSelectedAccounts(new Set())
      setShowBulkActions(false)
    } catch (error) {
      const msg = error instanceof Error ? error.message : "Failed to delete accounts"
      setBulkOperationError(msg)
    }
  }

  // Helper functions
  const generateCSV = (accounts: Account[]) => {
    const headers = ["Code", "Name", "Type", "Parent", "Description", "Status"]
    const rows = accounts.map(account => [
      account.code,
      account.name,
      account.accountType || "",
      account.parent?.name || "",
      account.description || "",
      account.isActive ? "Active" : "Inactive"
    ])
    
    return [headers, ...rows]
      .map(row => row.map(field => `"${field}"`).join(","))
      .join("\n")
  }

  const downloadCSV = (content: string, filename: string) => {
    const blob = new Blob([content], { type: "text/csv" })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = filename
    a.click()
    URL.revokeObjectURL(url)
  }

  // Extracted function for creating a new account

  const createAccount = async (form: typeof accountForm) => {
    // Validate required fields
    if (!form.accountTypeId) {
      throw new Error("Account type is required.");
    }
    if (!form.code || !form.name) {
      throw new Error("Account code and name are required.");
    }
    
    // Validate that the selected account type is one of the 5 standard types
    const selectedType = accountTypes.find(t => t.id === form.accountTypeId);
    if (!selectedType || !selectedType.code) {
      throw new Error("Invalid account type selected.");
    }
    
    const typeCode = selectedType.code.toUpperCase().trim();
    const isValidType = STANDARD_ACCOUNT_TYPES.some(std => std.code === typeCode);
    
    if (!isValidType) {
      throw new Error(`Invalid account type. Only the following types are allowed: ${STANDARD_ACCOUNT_TYPES.map(t => t.name).join(', ')}`);
    }
    
    // Map frontend field names to backend field names
    // Only send fields that the backend accountCreate schema expects
    const companyId = localStorage.getItem('company_id') || localStorage.getItem('companyId') || localStorage.getItem('company')
    
    const payload = {
      code: form.code.trim(),
      name: form.name.trim(),
      typeId: form.accountTypeId,
      parentId: form.parentId || undefined,
      companyId: companyId // Use real company ID from localStorage
    }
    
    return await accountingApi.chartOfAccountsApi.create(payload)
  }


  

  
  const handleAccountSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setAccountFormError(null);
    
    // Client-side validation before submission
    if (!accountForm.code || !accountForm.code.trim()) {
      setAccountFormError("Account code is required. Please enter a valid account code.");
      toast({
        title: "Validation Error",
        description: "Account code is required.",
        variant: "destructive",
        duration: 5000,
      });
      return;
    }
    
    if (!accountForm.name || !accountForm.name.trim()) {
      setAccountFormError("Account name is required. Please enter a valid account name.");
      toast({
        title: "Validation Error",
        description: "Account name is required.",
        variant: "destructive",
        duration: 5000,
      });
      return;
    }
    
    if (!accountForm.accountTypeId) {
      setAccountFormError("Account type is required. Please select a valid account type from the list.");
      toast({
        title: "Validation Error",
        description: "Account type is required. Please select one of the standard account types.",
        variant: "destructive",
        duration: 5000,
      });
      return;
    }
    
    // Validate account type is one of the 5 standard types
    const selectedType = accountTypes.find(t => t.id === accountForm.accountTypeId);
    if (!selectedType || !selectedType.code) {
      setAccountFormError("Invalid account type selected. Please select a valid account type.");
      toast({
        title: "Invalid Account Type",
        description: "The selected account type is not valid. Please select one of: Asset, Liability, Equity, Revenue, or Expense.",
        variant: "destructive",
        duration: 6000,
      });
      return;
    }
    
    const typeCode = selectedType.code.toUpperCase().trim();
    const isValidType = STANDARD_ACCOUNT_TYPES.some(std => std.code === typeCode);
    if (!isValidType) {
      setAccountFormError(`Invalid account type. Only the following types are allowed: ${STANDARD_ACCOUNT_TYPES.map(t => t.name).join(', ')}`);
      toast({
        title: "Invalid Account Type",
        description: `Only the following account types are allowed: ${STANDARD_ACCOUNT_TYPES.map(t => t.name).join(', ')}. Please select a valid type.`,
        variant: "destructive",
        duration: 6000,
      });
      return;
    }
    
    setAccountFormLoading(true);
    try {
      if (editingAccount) {
        // Map frontend field names to backend field names for update
        const updatePayload = {
          code: accountForm.code.trim(),
          name: accountForm.name.trim(),
          typeId: accountForm.accountTypeId,
          parentId: accountForm.parentId || undefined,
          isActive: accountForm.isActive
        }
        
        await accountingApi.chartOfAccountsApi.update(editingAccount.id, updatePayload)
      } else {
        await createAccount(accountForm)
      }
      
      // Store values before clearing state
      const wasEditing = !!editingAccount;
      const accountName = accountForm.name;
      
      // Close dialog and reset form immediately to unblock UI
      setShowAccountDialog(false);
      setEditingAccount(null);
      setAccountForm({
        name: "",
        code: "",
        description: "",
        accountTypeId: undefined,
        parentId: undefined,
        isActive: true
      });
      setAccountFormError(null);
      
      // Show success toast
      toast({
        title: wasEditing ? "Account Updated" : "Account Created",
        description: wasEditing 
          ? `Account "${accountName}" has been updated successfully. Refreshing page...`
          : `Account "${accountName}" has been created successfully. Refreshing page...`,
        variant: "default",
        duration: 2000,
      });
      
      // Store the current route and tab in sessionStorage before refresh
      // This ensures we return to the Chart of Accounts tab after refresh
      sessionStorage.setItem('returnToAccountingTab', 'chart-of-accounts')
      sessionStorage.setItem('returnToPath', window.location.pathname + window.location.search)
      
      // Refresh page after a short delay to allow toast to show and avoid freeze
      setTimeout(() => {
        window.location.reload()
      }, 500)
    } catch (error: any) {
      // Use the error formatting utility
      const { title, message, details } = formatApiError(error);
      
      // Handle specific error cases with clear messages
      let errorMsg = message;
      let errorTitle = title;
      
      if (message.includes('ACCOUNT_CODE_EXISTS') || 
          message.includes('duplicate') ||
          message.includes('already exists') ||
          message.includes('code already exists') ||
          message.includes('Unique constraint') ||
          message.includes('P2002')) {
        errorTitle = "Duplicate Account Code";
        errorMsg = `An account with code "${accountForm.code}" already exists. Please use a different account code.`;
      } else if (message.includes('ACCOUNT_NAME_EXISTS') || 
                 message.includes('name already exists')) {
        errorTitle = "Duplicate Account Name";
        errorMsg = `An account with name "${accountForm.name}" already exists. Please use a different account name.`;
      } else if (message.includes('validation_error') || 
                 message.includes('Invalid input') ||
                 message.includes('expected string') ||
                 message.includes('required')) {
        errorTitle = "Validation Error";
        errorMsg = message || "Please check all required fields are filled correctly.";
      } else if (message.includes('network') || 
                 message.includes('fetch') ||
                 message.includes('timeout')) {
        errorTitle = "Connection Error";
        errorMsg = "Unable to connect to the server. Please check your internet connection and try again.";
      } else if (!message || message === 'Unknown error') {
        errorTitle = "Error Creating Account";
        errorMsg = "An unexpected error occurred while creating the account. Please try again or contact support if the problem persists.";
      }
      
      // Add details if available
      if (details && !errorMsg.includes(details)) {
        errorMsg += `\n\nDetails: ${details}`;
      }
      
      setAccountFormError(errorMsg);
      
      // Show toast notification with clear error message
      toast({
        title: errorTitle,
        description: errorMsg.length > 100 ? errorMsg.substring(0, 100) + "..." : errorMsg,
        variant: "destructive",
        duration: 8000,
      });
    } finally {
      setAccountFormLoading(false);
    }
  };

  const [typeFormError, setTypeFormError] = useState<string | null>(null);

  const handleTypeSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setTypeFormError(null)
    
    // Client-side validation before submission
    if (!typeForm.code || !typeForm.code.trim()) {
      setTypeFormError("Account type code is required. Please select a valid account type code.");
      toast({
        title: "Validation Error",
        description: "Account type code is required.",
        variant: "destructive",
        duration: 5000,
      });
      return;
    }
    
    if (!typeForm.name || !typeForm.name.trim()) {
      setTypeFormError("Account type name is required. Please select a valid account type name.");
      toast({
        title: "Validation Error",
        description: "Account type name is required.",
        variant: "destructive",
        duration: 5000,
      });
      return;
    }
    
    // Validate that the selected code is one of the 5 standard types
    const codeUpper = typeForm.code.toUpperCase().trim();
    const isValidCode = STANDARD_ACCOUNT_TYPES.some(std => std.code === codeUpper);
    
    if (!isValidCode) {
      setTypeFormError(`Invalid account type code. Only the following codes are allowed: ${STANDARD_ACCOUNT_TYPES.map(t => t.code).join(', ')}`);
      toast({
        title: "Invalid Account Type Code",
        description: `Only the following account type codes are allowed: ${STANDARD_ACCOUNT_TYPES.map(t => t.code).join(', ')}.`,
        variant: "destructive",
        duration: 6000,
      });
      return;
    }
    
    // Validate that the selected name matches one of the 5 standard types
    const nameNormalized = typeForm.name.trim();
    const isValidName = STANDARD_ACCOUNT_TYPES.some(std => 
      std.name.toLowerCase() === nameNormalized.toLowerCase() ||
      std.name.toLowerCase() + 's' === nameNormalized.toLowerCase() ||
      std.name.toLowerCase() === nameNormalized.toLowerCase().replace(/s$/, '')
    );
    
    if (!isValidName) {
      setTypeFormError(`Invalid account type name. Only the following names are allowed: ${STANDARD_ACCOUNT_TYPES.map(t => t.name).join(', ')}`);
      toast({
        title: "Invalid Account Type Name",
        description: `Only the following account type names are allowed: ${STANDARD_ACCOUNT_TYPES.map(t => t.name).join(', ')}.`,
        variant: "destructive",
        duration: 6000,
      });
      return;
    }
    
    // Ensure code and name match (e.g., ASSET should match Asset)
    const matchedType = STANDARD_ACCOUNT_TYPES.find(std => std.code === codeUpper);
    if (matchedType) {
      const nameMatches = matchedType.name.toLowerCase() === nameNormalized.toLowerCase() ||
                         matchedType.name.toLowerCase() + 's' === nameNormalized.toLowerCase() ||
                         matchedType.name.toLowerCase() === nameNormalized.toLowerCase().replace(/s$/, '');
      
      if (!nameMatches) {
        setTypeFormError(`Account type code "${codeUpper}" should match name "${matchedType.name}", but you selected "${nameNormalized}". Please select matching code and name.`);
        toast({
          title: "Code and Name Mismatch",
          description: `Account type code "${codeUpper}" should match name "${matchedType.name}".`,
          variant: "destructive",
          duration: 6000,
        });
        return;
      }
    }
    
    try {
      const payload: any = {
        code: codeUpper,
        name: matchedType?.name || nameNormalized,
        normalBalance: typeForm.normalBalance || "debit"
      };
      
      if (editingType) {
        await accountingApi.accountTypesApi.update(editingType.id, payload)
      } else {
        await accountingApi.accountTypesApi.create(payload)
      }
      
      // Store values before clearing state
      const wasEditingType = !!editingType;
      const typeDisplayName = matchedType?.name || nameNormalized;
      
      // Close dialog and reset form immediately to unblock UI
      setShowTypeDialog(false)
      setEditingType(null)
      setTypeForm({
        code: "",
        name: "",
        description: "",
        normalBalance: "debit",
        category: ""
      })
      setTypeFormError(null)
      
      // Show success toast
      toast({
        title: wasEditingType ? "Account Type Updated" : "Account Type Created",
        description: wasEditingType 
          ? `Account type "${typeDisplayName}" has been updated successfully. Refreshing page...`
          : `Account type "${typeDisplayName}" has been created successfully. Refreshing page...`,
        variant: "default",
        duration: 2000,
      });
      
      // Store the current route and tab in sessionStorage before refresh
      // This ensures we return to the Chart of Accounts tab after refresh
      sessionStorage.setItem('returnToAccountingTab', 'chart-of-accounts')
      sessionStorage.setItem('returnToPath', window.location.pathname + window.location.search)
      
      // Refresh page after a short delay to allow toast to show and avoid freeze
      setTimeout(() => {
        window.location.reload()
      }, 500)
    } catch (error: any) {
      // Use the error formatting utility
      const { title, message, details } = formatApiError(error);
      
      // Handle specific error cases with clear messages
      let errorMsg = message;
      let errorTitle = title;
      
      if (message.includes('ACCOUNT_TYPE_EXISTS') || 
          message.includes('duplicate') ||
          message.includes('already exists') ||
          message.includes('Unique constraint') ||
          message.includes('P2002')) {
        errorTitle = "Duplicate Account Type";
        errorMsg = `An account type with code "${codeUpper}" or name "${nameNormalized}" already exists. Please check existing account types.`;
      } else if (message.includes('validation_error') || 
                 message.includes('Invalid input') ||
                 message.includes('expected string') ||
                 message.includes('required')) {
        errorTitle = "Validation Error";
        errorMsg = message || "Please check all required fields are filled correctly.";
      } else if (message.includes('network') || 
                 message.includes('fetch') ||
                 message.includes('timeout')) {
        errorTitle = "Connection Error";
        errorMsg = "Unable to connect to the server. Please check your internet connection and try again.";
      } else if (!message || message === 'Unknown error') {
        errorTitle = "Error Creating Account Type";
        errorMsg = "An unexpected error occurred while creating the account type. Please try again or contact support if the problem persists.";
      }
      
      // Add details if available
      if (details && !errorMsg.includes(details)) {
        errorMsg += `\n\nDetails: ${details}`;
      }
      
      setTypeFormError(errorMsg);
      
      // Show toast notification with clear error message
      toast({
        title: errorTitle,
        description: errorMsg.length > 100 ? errorMsg.substring(0, 100) + "..." : errorMsg,
        variant: "destructive",
        duration: 8000,
      });
    }
  }

  const editAccount = (account: Account) => {
    setEditingAccount(account)
    setAccountForm({
      name: account.name,
      code: account.code,
      description: account.description || "",
      accountTypeId: account.accountTypeId,
      parentId: account.parentId || undefined,
      isActive: account.isActive
    })
    setShowAccountDialog(true)
  }

  const editType = (type: AccountType) => {
    setEditingType(type)
    setTypeForm({
      code: type.code || "",
      name: type.name,
      description: type.description || "",
      normalBalance: type.normalBalance,
      category: type.category || ""
    })
    setShowTypeDialog(true)
  }

  const handleDeleteAccount = async (accountId: string) => {
    if (!confirm("Are you sure you want to delete this account?")) return
    
    try {
      await accountingApi.chartOfAccountsApi.delete(accountId)
      await manualLoadData()
    } catch (error) {
      const msg = error instanceof Error ? error.message : "Failed to delete account"
      setError(msg)
      }
  }

  const handleDeleteType = async (typeId: string) => {
    if (!confirm("Are you sure you want to delete this account type?")) return
    
    try {
      await accountingApi.accountTypesApi.delete(typeId)
      await manualLoadData()
    } catch (error) {
      const msg = error instanceof Error ? error.message : "Failed to delete account type"
      setError(msg)
    }
  }

  const toggleAccountSelection = (accountId: string) => {
    const newSelection = new Set(selectedAccounts)
    if (newSelection.has(accountId)) {
      newSelection.delete(accountId)
    } else {
      newSelection.add(accountId)
    }
    setSelectedAccounts(newSelection)
    setShowBulkActions(newSelection.size > 0)
  }

  const selectAllAccounts = () => {
    if (selectedAccounts.size === filteredAccounts.length) {
      setSelectedAccounts(new Set())
      setShowBulkActions(false)
    } else {
      setSelectedAccounts(new Set(filteredAccounts.map(account => account.id)))
      setShowBulkActions(true)
    }
  }

  const toggleNodeExpansion = (nodeId: string) => {
    const newExpanded = new Set(expandedNodes)
    if (newExpanded.has(nodeId)) {
      newExpanded.delete(nodeId)
    } else {
      newExpanded.add(nodeId)
    }
    setExpandedNodes(newExpanded)
  }

  const renderAccountNode = (node: AccountTreeNode): React.ReactNode => {
    const hasChildren = node.children.length > 0
    const isExpanded = expandedNodes.has(node.id)
    const isSelected = selectedAccounts.has(node.id)
    
    return (
      <div key={node.id} className="space-y-1">
        <div 
          className={`flex items-center space-x-2 p-2 rounded-lg hover:bg-gray-50 ${
            isSelected ? "bg-blue-50 border-blue-200" : ""
          }`}
          style={{ paddingLeft: `${node.level * 20 + 8}px` }}
        >
          {hasChildren && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0"
              onClick={() => toggleNodeExpansion(node.id)}
            >
              {isExpanded ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </Button>
          )}
          
          {!hasChildren && <div className="w-6" />}
          
          <Checkbox
            checked={isSelected}
            onCheckedChange={() => toggleAccountSelection(node.id)}
          />
          
          <div className="flex-1 flex items-center justify-between">
            <div>
              <div className="flex items-center space-x-2">
                {showAccountCodes && <span className="font-medium">{node.code}</span>}
                <span>{node.name}</span>
                {!node.isActive && (
                  <Badge variant="secondary"><TranslatedText text="Inactive" /></Badge>
                )}
              </div>
              {node.description && node.description !== "" && (
                <p className="text-sm text-gray-600">{node.description}</p>
              )}
            </div>
            
            <div className="flex items-center space-x-2">
              <Badge variant="outline">
                {accountTypes.find(t => t.id === node.accountTypeId)?.name || "Unknown Type"}
              </Badge>
              
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => editAccount(node)}>
                    <Edit className="mr-2 h-4 w-4" />
                    <TranslatedText text="Edit" />
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => {}}>
                    <Copy className="mr-2 h-4 w-4" />
                    <TranslatedText text="Duplicate" />
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => {}}>
                    <Eye className="mr-2 h-4 w-4" />
                    <TranslatedText text="View Transactions" />
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={() => handleDeleteAccount(node.id)}
                    className="text-red-600"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    <TranslatedText text="Delete" />
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
        
        {hasChildren && isExpanded && (
          <div className="space-y-1">
            {node.children.map(child => renderAccountNode(child))}
          </div>
        )}
      </div>
    )
  }

  if (loading) {
    return (
      <div className="min-h-[400px] flex items-center justify-center p-8">
        <div className="text-center space-y-4">
          <MoonLoader size="xl" color="teal" />
          <div className="space-y-2">
            <h3 className="text-lg font-semibold text-gray-900">Loading Chart of Accounts</h3>
            <p className="text-gray-500">Please wait while we fetch your account data...</p>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-[400px] flex items-center justify-center p-8">
        <div className="text-center space-y-4 max-w-md">
          <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
            <AlertCircle className="h-8 w-8 text-red-600" />
          </div>
          <div className="space-y-2">
            <h3 className="text-lg font-semibold text-gray-900">Something went wrong</h3>
            <p className="text-gray-500">{error}</p>
          </div>
          <Button 
            onClick={manualLoadData} 
            variant="outline"
            className="mt-4"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Try Again
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Enhanced Header with Stats */}
      {summary && (
        <div className="space-y-6">
          {/* Main Stats Grid */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200 hover:shadow-md transition-all duration-200">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-medium text-blue-800">Total Accounts</CardTitle>
                  <Building2 className="h-5 w-5 text-blue-600" />
                </div>
            </CardHeader>
            <CardContent>
                <div className="text-3xl font-bold text-blue-900">{summary.totalAccounts}</div>
                <div className="flex items-center gap-2 mt-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <p className="text-sm text-blue-700">
                    {summary.activeAccounts} active accounts
                  </p>
                </div>
            </CardContent>
          </Card>
          
            <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200 hover:shadow-md transition-all duration-200">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-medium text-green-800">Account Types</CardTitle>
                  <Layers className="h-5 w-5 text-green-600" />
                </div>
            </CardHeader>
            <CardContent>
                <div className="text-3xl font-bold text-green-900">{summary.totalAccountTypes}</div>
                <p className="text-sm text-green-700 mt-2">Categories defined</p>
            </CardContent>
          </Card>
          
            <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200 hover:shadow-md transition-all duration-200">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-medium text-purple-800">Hierarchy Levels</CardTitle>
                  <FolderOpen className="h-5 w-5 text-purple-600" />
                </div>
            </CardHeader>
            <CardContent>
                <div className="text-3xl font-bold text-purple-900">{summary.maxDepth}</div>
                <p className="text-sm text-purple-700 mt-2">Maximum depth</p>
            </CardContent>
          </Card>
          
            <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200 hover:shadow-md transition-all duration-200">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-medium text-orange-800">Last Updated</CardTitle>
                  <Clock className="h-5 w-5 text-orange-600" />
                </div>
            </CardHeader>
            <CardContent>
                <div className="text-2xl font-bold text-orange-900">{summary.lastUpdated}</div>
                <p className="text-sm text-orange-700 mt-2">Recent activity</p>
              </CardContent>
            </Card>
          </div>
          
          {/* Quick Actions Bar */}
          <Card className="bg-gradient-to-r from-slate-50 to-gray-50 border-slate-200">
            <CardContent className="p-4">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                  <Zap className="h-4 w-4 text-amber-500" />
                  <span className="text-sm font-medium text-slate-700"><TranslatedText text="Quick Actions" /></span>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" onClick={() => setShowAccountDialog(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    <TranslatedText text="New Account" />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setShowTypeDialog(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    <TranslatedText text="New Type" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Enhanced Main Content with Tabs */}
      <Tabs defaultValue="accounts" className="space-y-6">
        <div className="bg-white rounded-lg border border-slate-200 p-1 shadow-sm">
          <TabsList className="grid w-full grid-cols-3 bg-transparent">
            <TabsTrigger 
              value="accounts" 
              className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700 data-[state=active]:shadow-sm"
            >
              <BarChart3 className="h-4 w-4 mr-2" />
              <TranslatedText text="Accounts" />
            </TabsTrigger>
            <TabsTrigger 
              value="types"
              className="data-[state=active]:bg-green-50 data-[state=active]:text-green-700 data-[state=active]:shadow-sm"
            >
              <Layers className="h-4 w-4 mr-2" />
              <TranslatedText text="Account Types" />
            </TabsTrigger>
            <TabsTrigger 
              value="settings"
              className="data-[state=active]:bg-purple-50 data-[state=active]:text-purple-700 data-[state=active]:shadow-sm"
            >
              <Settings className="h-4 w-4 mr-2" />
              <TranslatedText text="Settings" />
            </TabsTrigger>
        </TabsList>
        </div>

        <TabsContent value="accounts" className="space-y-6">
          {/* Search and filters */}
          <Card className="shadow-sm border-slate-200">
            <CardHeader className="bg-gradient-to-r from-slate-50 to-gray-50 border-b border-slate-200">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <CardTitle className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                    <BarChart3 className="h-5 w-5 text-blue-600" />
                    <TranslatedText text="Chart of Accounts" />
                  </CardTitle>
                  <CardDescription className="text-gray-600 mt-1">
                <TranslatedText text="Manage your organization's account structure and hierarchy" />
              </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                    {filteredAccounts.length} <TranslatedText text="accounts" />
                  </Badge>
                  <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                    {accounts.filter(a => a.isActive).length} <TranslatedText text="active" />
                  </Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Enhanced Search and Filter Controls */}
              <div className="space-y-4">
                {/* Primary Search Bar */}
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                    <Input
                    placeholder={tSync("Search accounts by name, code, description, or type...")}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 h-12 text-base"
                  />
                  {searchTerm && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 h-8 w-8 p-0"
                      onClick={() => setSearchTerm("")}
                    >
                      <XCircle className="h-4 w-4" />
                    </Button>
                  )}
                  </div>
                
                {/* Filter Controls */}
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex items-center gap-2">
                    <Filter className="h-4 w-4 text-gray-500" />
                    <span className="text-sm font-medium text-gray-700"><TranslatedText text="Filters:" /></span>
                </div>
                
                <Select value={filterType} onValueChange={setFilterType}>
                    <SelectTrigger className="w-[160px]">
                    <SelectValue placeholder={tSync("Account Type")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all"><TranslatedText text="All Types" /></SelectItem>
                    {accountTypes.filter(type => type.id && type.id !== "").map(type => (
                      <SelectItem key={type.id} value={type.id}>
                          {type.name || type.code}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="w-[140px]">
                    <SelectValue placeholder={tSync("Status")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all"><TranslatedText text="All Status" /></SelectItem>
                    <SelectItem value="active"><TranslatedText text="Active" /></SelectItem>
                    <SelectItem value="inactive"><TranslatedText text="Inactive" /></SelectItem>
                  </SelectContent>
                </Select>
                
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="show-inactive"
                      checked={showInactive}
                      onCheckedChange={setShowInactive}
                    />
                    <Label htmlFor="show-inactive" className="text-sm">
                      <TranslatedText text="Show Inactive" />
                    </Label>
                  </div>
                  </div>
                  
                {/* View Mode Selector */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-700"><TranslatedText text="View:" /></span>
                    <div className="flex bg-gray-100 rounded-lg p-1">
                      {["list", "tree", "grid"].map((mode) => (
                        <Button
                          key={mode}
                          variant={viewMode === mode ? "default" : "ghost"}
                          size="sm"
                          onClick={() => setViewMode(mode as "list" | "tree" | "grid")}
                          className="h-8 px-3 text-xs"
                        >
                          {mode === "list" && <FileText className="h-3 w-3 mr-1" />}
                          {mode === "tree" && <FolderOpen className="h-3 w-3 mr-1" />}
                          {mode === "grid" && <Layers className="h-3 w-3 mr-1" />}
                          {mode === "list" ? <TranslatedText text="List" /> : mode === "tree" ? <TranslatedText text="Tree" /> : <TranslatedText text="Grid" />}
                        </Button>
                      ))}
                </div>
              </div>

                <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={manualLoadData}
                      disabled={loading}
                    >
                      <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
                      <TranslatedText text="Refresh" />
                    </Button>
                  </div>
                </div>
              </div>

              {/* Enhanced Action Buttons */}
              <div className="flex flex-wrap items-center justify-between gap-4 p-4 bg-gradient-to-r from-slate-50 to-gray-50 rounded-lg border border-slate-200">
                <div className="flex flex-wrap items-center gap-2">
                  <Button 
                    onClick={() => setShowAccountDialog(true)}
                    className="bg-blue-600 hover:bg-blue-700 text-white shadow-sm"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    <TranslatedText text="Add Account" />
                  </Button>
                  
                  <Button 
                    variant="outline" 
                    onClick={() => setShowTypeDialog(true)}
                    className="border-green-200 text-green-700 hover:bg-green-50 hover:text-green-800"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    <TranslatedText text="Add Type" />
                  </Button>
                  
                  <Button 
                    variant="outline" 
                    onClick={() => handleBulkExport(accounts.map(a => a.id))}
                    className="border-orange-200 text-orange-700 hover:bg-orange-50 hover:text-orange-800"
                  >
                    <Download className="mr-2 h-4 w-4" />
                    <TranslatedText text="Export All" />
                  </Button>
                </div>
                
                <div className="flex items-center gap-3">
                  {selectedAccounts.size > 0 && (
                    <div className="flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-800 rounded-full">
                      <CheckCircle className="h-4 w-4" />
                      <span className="text-sm font-medium">
                      {selectedAccounts.size} <TranslatedText text="selected" />
                      </span>
                    </div>
                  )}
                  
                  <div className="text-sm text-gray-500">
                    {filteredAccounts.length} <TranslatedText text="of" /> {accounts.length} <TranslatedText text="accounts" />
                  </div>
                </div>
              </div>

              {/* Enhanced Bulk Actions Bar */}
              {showBulkActions && (
                <div className="space-y-3">
                  {bulkOperationError && (
                    <Alert variant="destructive">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>{bulkOperationError}</AlertDescription>
                    </Alert>
                  )}
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200 p-4 shadow-sm">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2 px-3 py-2 bg-blue-100 text-blue-800 rounded-full">
                          <CheckCircle className="h-4 w-4" />
                          <span className="font-medium">
                    {selectedAccounts.size} accounts selected
                  </span>
                        </div>
                        <span className="text-sm text-blue-700">
                          Choose an action to perform on selected accounts
                        </span>
                      </div>
                      
                      <div className="flex flex-wrap items-center gap-2">
                    {bulkActions.map(action => (
                      <Button
                        key={action.id}
                        variant={action.destructive ? "destructive" : "outline"}
                        size="sm"
                        onClick={() => action.action(Array.from(selectedAccounts))}
                            className={action.destructive ? "bg-red-600 hover:bg-red-700" : "hover:shadow-sm"}
                      >
                        {action.icon}
                        <span className="ml-2">{action.label}</span>
                      </Button>
                    ))}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setSelectedAccounts(new Set())
                        setShowBulkActions(false)
                            setBulkOperationError(null)
                      }}
                          className="text-gray-600 hover:text-gray-800"
                    >
                          Clear Selection
                    </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Account list/tree */}
              <div className="space-y-4">
                {/* Enhanced Select All Section */}
                <div className="flex items-center justify-between p-3 bg-gradient-to-r from-slate-50 to-gray-50 rounded-lg border border-slate-200">
                  <div className="flex items-center gap-3">
                  <Checkbox
                    checked={selectedAccounts.size === filteredAccounts.length && filteredAccounts.length > 0}
                    onCheckedChange={selectAllAccounts}
                      className="h-5 w-5"
                  />
                    <div>
                      <span className="text-sm font-medium text-gray-700">
                    <TranslatedText text="Select All" /> ({filteredAccounts.length} <TranslatedText text="accounts" />)
                  </span>
                      {selectedAccounts.size > 0 && (
                        <p className="text-xs text-gray-500 mt-1">
                          {selectedAccounts.size} <TranslatedText text="of" /> {filteredAccounts.length} <TranslatedText text="selected" />
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <span><TranslatedText text="View:" /></span>
                    <span className="font-medium">{viewMode === "list" ? <TranslatedText text="List" /> : viewMode === "tree" ? <TranslatedText text="Tree" /> : <TranslatedText text="Grid" />}</span>
                  </div>
                </div>

                {/* Accounts display */}
                {viewMode === "tree" ? (
                  <div className="space-y-1">
                    {accountTree.map(node => renderAccountNode(node))}
                  </div>
                ) : viewMode === "grid" ? (
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {filteredAccounts.map(account => (
                      <Card key={account.id} className={`cursor-pointer transition-colors ${
                        selectedAccounts.has(account.id) ? "border-blue-500 bg-blue-50" : ""
                      }`}>
                        <CardHeader className="pb-2">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                              <Checkbox
                                checked={selectedAccounts.has(account.id)}
                                onCheckedChange={() => toggleAccountSelection(account.id)}
                              />
                              {showAccountCodes && (
                                <Badge variant="outline">
                                  {account.code}
                                </Badge>
                              )}
                            </div>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent>
                                <DropdownMenuItem onClick={() => editAccount(account)}>
                                  <Edit className="mr-2 h-4 w-4" />
                                  <TranslatedText text="Edit" />
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleDeleteAccount(account.id)}>
                                  <Trash2 className="mr-2 h-4 w-4" />
                                  <TranslatedText text="Delete" />
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                          <CardTitle className="text-lg">{account.name}</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-muted-foreground"><TranslatedText text="Type" /></span>
                              <Badge variant="secondary">
                                {accountTypes.find(t => t.id === account.accountTypeId)?.name || <TranslatedText text="Unknown Type" />}
                              </Badge>
                            </div>
                            {account.description && account.description !== "" && (
                              <p className="text-sm text-muted-foreground">
                                {account.description}
                              </p>
                            )}
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-muted-foreground"><TranslatedText text="Status" /></span>
                              <Badge variant={account.isActive ? "default" : "secondary"}>
                                {account.isActive ? <TranslatedText text="Active" /> : <TranslatedText text="Inactive" />}
                              </Badge>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  // List view
                  <div className="space-y-2">
                    {filteredAccounts.map(account => (
                      <div
                        key={account.id}
                        className={`flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 ${
                          selectedAccounts.has(account.id) ? "border-blue-500 bg-blue-50" : ""
                        }`}
                      >
                        <div className="flex items-center space-x-3">
                          <Checkbox
                            checked={selectedAccounts.has(account.id)}
                            onCheckedChange={() => toggleAccountSelection(account.id)}
                          />
                          <div>
                            <div className="flex items-center space-x-2">
                              {showAccountCodes && <span className="font-medium">{account.code}</span>}
                              <span>{account.name}</span>
                              {!account.isActive && (
                                <Badge variant="secondary"><TranslatedText text="Inactive" /></Badge>
                              )}
                            </div>
                            {account.description && account.description !== "" && (
                              <p className="text-sm text-muted-foreground">
                                {account.description}
                              </p>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          <Badge variant="outline">
                            {accountTypes.find(t => t.id === account.accountTypeId)?.name || "Unknown Type"}
                          </Badge>
                          
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => editAccount(account)}>
                                <Edit className="mr-2 h-4 w-4" />
                                <TranslatedText text="Edit" />
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => {}}>
                                <Copy className="mr-2 h-4 w-4" />
                                <TranslatedText text="Duplicate" />
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => {}}>
                                <Eye className="mr-2 h-4 w-4" />
                                <TranslatedText text="View Transactions" />
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem 
                                onClick={() => handleDeleteAccount(account.id)}
                                className="text-red-600"
                              >
                                <Trash2 className="mr-2 h-4 w-4" />
                                <TranslatedText text="Delete" />
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Enhanced Pagination */}
              {accountsWithPagination && accountsWithPagination.pagination && (
                <div className="bg-gradient-to-r from-slate-50 to-gray-50 rounded-lg border border-slate-200 p-4">
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <FileText className="h-4 w-4" />
                      <span>
                        Showing <span className="font-medium text-gray-900">
                          {((accountsWithPagination.pagination.page - 1) * accountsWithPagination.pagination.pageSize) + 1}
                        </span> to <span className="font-medium text-gray-900">
                          {Math.min(accountsWithPagination.pagination.page * accountsWithPagination.pagination.pageSize, accountsWithPagination.pagination.totalCount)}
                        </span> of <span className="font-medium text-gray-900">
                          {accountsWithPagination.pagination.totalCount}
                        </span> accounts
                      </span>
                  </div>
                    
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(1)}
                      disabled={!accountsWithPagination.pagination.hasPrev}
                        className="h-8 w-8 p-0 hover:bg-blue-50 hover:border-blue-200"
                    >
                      <ChevronsLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(currentPage - 1)}
                      disabled={!accountsWithPagination.pagination.hasPrev}
                        className="h-8 w-8 p-0 hover:bg-blue-50 hover:border-blue-200"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                      
                      <div className="flex items-center gap-1 px-3 py-1 bg-white border border-slate-200 rounded-md">
                        <span className="text-sm font-medium text-gray-900">
                          Page {accountsWithPagination.pagination.page}
                    </span>
                        <span className="text-sm text-gray-500">of {accountsWithPagination.pagination.totalPages}</span>
                      </div>
                      
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={!accountsWithPagination.pagination.hasNext}
                        className="h-8 w-8 p-0 hover:bg-blue-50 hover:border-blue-200"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(accountsWithPagination.pagination.totalPages)}
                      disabled={!accountsWithPagination.pagination.hasNext}
                        className="h-8 w-8 p-0 hover:bg-blue-50 hover:border-blue-200"
                    >
                      <ChevronsRight className="h-4 w-4" />
                    </Button>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="types" className="space-y-6">
          <Card className="shadow-sm border-slate-200">
            <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50 border-b border-green-200">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <CardTitle className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                    <Layers className="h-5 w-5 text-green-600" />
                    Account Types
                  </CardTitle>
                  <CardDescription className="text-gray-600 mt-1">
                Manage account categories and their normal balance behavior
              </CardDescription>
                </div>
                <Button 
                  onClick={() => setShowTypeDialog(true)}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Type
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              {accountTypes.length === 0 ? (
                <div className="text-center py-12">
                  <Layers className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No account types yet</h3>
                  <p className="text-gray-500 mb-4">Create your first account type to get started</p>
                  <Button onClick={() => setShowTypeDialog(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Create Account Type
                  </Button>
                </div>
              ) : (
                <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {accountTypes.map(type => (
                    <Card key={type.id} className="hover:shadow-md transition-all duration-200 border-slate-200">
                      <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                          <CardTitle className="text-lg text-gray-900">{type.name}</CardTitle>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0 hover:bg-slate-100">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent>
                            <DropdownMenuItem onClick={() => editType(type)}>
                              <Edit className="mr-2 h-4 w-4" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => handleDeleteType(type.id)}
                              className="text-red-600"
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-3">
                          <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                            <span className="text-sm font-medium text-gray-700">Normal Balance</span>
                            <Badge 
                              variant={type.normalBalance === "debit" ? "default" : "secondary"}
                              className={type.normalBalance === "debit" ? "bg-blue-100 text-blue-800" : "bg-gray-100 text-gray-800"}
                            >
                            {type.normalBalance === "debit" ? "Debit" : "Credit"}
                          </Badge>
                        </div>
                          {type.description && type.description !== "" && (
                            <div className="p-2 bg-slate-50 rounded-lg">
                              <p className="text-sm text-gray-600">{type.description}</p>
                            </div>
                          )}
                          {type.category && type.category !== "" && (
                            <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                              <span className="text-sm font-medium text-gray-700">Category</span>
                              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                                {type.category}
                              </Badge>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="settings" className="space-y-6">
          <Card className="shadow-sm border-slate-200">
            <CardHeader className="bg-gradient-to-r from-purple-50 to-violet-50 border-b border-purple-200">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <CardTitle className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                    <Settings className="h-5 w-5 text-purple-600" />
                    Chart of Accounts Settings
                  </CardTitle>
                  <CardDescription className="text-gray-600 mt-1">
                    Configure general settings and preferences for your chart of accounts
              </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              <div className="space-y-8">
                {/* Display Options */}
              <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                    <h3 className="text-lg font-semibold text-gray-900"><TranslatedText text="Display Options" /></h3>
                  </div>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                      <div>
                        <Label className="text-base font-medium"><TranslatedText text="Show Account Codes" /></Label>
                        <p className="text-sm text-gray-600 mt-1"><TranslatedText text="Display account codes alongside names for better identification" /></p>
                      </div>
                      <Switch checked={showAccountCodes} onCheckedChange={setShowAccountCodes} />
                    </div>
                    <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                      <div>
                        <Label className="text-base font-medium"><TranslatedText text="Show Inactive Accounts" /></Label>
                        <p className="text-sm text-gray-600 mt-1"><TranslatedText text="Include inactive accounts in lists and searches" /></p>
                      </div>
                      <Switch checked={showInactive} onCheckedChange={setShowInactive} />
                    </div>
                    <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                      <div>
                        <Label className="text-base font-medium"><TranslatedText text="Auto-expand Tree" /></Label>
                        <p className="text-sm text-gray-600 mt-1"><TranslatedText text="Automatically expand account hierarchy when switching to tree view" /></p>
                      </div>
                      <Switch checked={autoExpandTree} onCheckedChange={setAutoExpandTree} />
                    </div>
                  </div>
                </div>
                
                <Separator className="bg-slate-200" />
                
                {/* Export */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    <h3 className="text-lg font-semibold text-gray-900"><TranslatedText text="Export" /></h3>
                  </div>
                  <div className="grid gap-6 md:grid-cols-1">
                    <Card className="border-slate-200 hover:shadow-md transition-all duration-200">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-base flex items-center gap-2">
                          <Download className="h-4 w-4 text-green-600" />
                          <TranslatedText text="Export Accounts" />
                        </CardTitle>
                        <CardDescription><TranslatedText text="Download your complete chart of accounts" /></CardDescription>
                      </CardHeader>
                      <CardContent>
                        <Button className="w-full bg-green-600 hover:bg-green-700" onClick={() => handleBulkExport(accounts.map(a => a.id))}>
                          <Download className="mr-2 h-4 w-4" />
                          <TranslatedText text="Export CSV" />
                        </Button>
                      </CardContent>
                    </Card>
                  </div>
                </div>
                
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Account Dialog */}
      <Dialog 
        open={showAccountDialog} 
        onOpenChange={(open) => {
          // Always allow closing - don't block UI
          if (!open) {
            setShowAccountDialog(false);
            // Only reset if not currently loading (to preserve form state during save)
            if (!accountFormLoading) {
              setEditingAccount(null);
              setAccountFormError(null);
              setAccountForm({
                name: "",
                code: "",
                description: "",
                accountTypeId: undefined,
                parentId: undefined,
                isActive: true
              });
            }
          } else {
            // Opening dialog - just set the state
            setShowAccountDialog(true);
          }
        }}
      >
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>
              {editingAccount ? <TranslatedText text="Edit Account" /> : <TranslatedText text="Add New Account" />}
            </DialogTitle>
            <DialogDescription>
              {editingAccount ? <TranslatedText text="Update account information" /> : <TranslatedText text="Create a new account in your chart of accounts" />}
            </DialogDescription>
          </DialogHeader>
          {(() => {
            try {
              return (
                <form onSubmit={handleAccountSubmit} className="space-y-4">
                  {accountFormError && (
                    <Alert variant="destructive" className="border-red-500 bg-red-50">
                      <AlertCircle className="h-4 w-4 text-red-600" />
                      <AlertDescription className="text-red-800 font-medium whitespace-pre-wrap">
                        {accountFormError}
                      </AlertDescription>
                    </Alert>
                  )}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="account-code"><TranslatedText text="Account Code" /></Label>
                      <Input
                        id="account-code"
                        value={accountForm.code || ""}
                        onChange={(e) => setAccountForm({...accountForm, code: e.target.value})}
                        placeholder={tSync("e.g., 1000")}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="account-name"><TranslatedText text="Account Name" /></Label>
                      <Input
                        id="account-name"
                        value={accountForm.name || ""}
                        onChange={(e) => setAccountForm({...accountForm, name: e.target.value})}
                        placeholder={tSync("e.g., Cash")}
                        required
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="account-type"><TranslatedText text="Account Type" /> <span className="text-red-500">*</span></Label>
                    {(() => {
                      // Filter to only show the 5 standard account types
                      const validAccountTypes = accountTypes.filter(type => {
                        if (!type.id || !type.code) return false;
                        const typeCode = type.code.toUpperCase().trim();
                        return STANDARD_ACCOUNT_TYPES.some(std => std.code === typeCode);
                      });

                      if (validAccountTypes.length === 0) {
                        return (
                          <Alert variant="destructive">
                            <AlertCircle className="h-4 w-4" />
                            <AlertDescription>
                              No valid account types found. Please ensure the following account types exist: Asset, Liability, Equity, Revenue, Expense.
                            </AlertDescription>
                          </Alert>
                        );
                      }

                      return (
                        <Select
                          value={accountForm.accountTypeId || ""}
                          onValueChange={(value) => {
                            if (value && validAccountTypes.some(t => t.id === value)) {
                              setAccountForm({ ...accountForm, accountTypeId: value });
                              setAccountFormError(null);
                            }
                          }}
                          required
                        >
                          <SelectTrigger id="account-type">
                            <SelectValue placeholder={tSync("Select an account type")} />
                          </SelectTrigger>
                          <SelectContent>
                            {validAccountTypes.map(type => {
                              const stdType = STANDARD_ACCOUNT_TYPES.find(std => std.code === type.code?.toUpperCase().trim());
                              return (
                                <SelectItem key={type.id} value={type.id}>
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium">{stdType?.name || type.name || type.code}</span>
                                    <span className="text-xs text-muted-foreground">({type.code})</span>
                                  </div>
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                      );
                    })()}
                    {!accountForm.accountTypeId && accountFormError && (
                      <p className="text-sm text-red-500 mt-1">Please select a valid account type</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="parent-account"><TranslatedText text="Parent Account (Optional)" /></Label>
                    <select
                      id="parent-account"
                      className="w-full border rounded px-2 py-2"
                      value={accountForm.parentId || "none"}
                                              onChange={e => setAccountForm({ ...accountForm, parentId: e.target.value === "none" ? undefined : e.target.value })}
                    >
                      <option value="none"><TranslatedText text="No Parent" /></option>
                      {accounts
                        .filter(account => account.id && account.id !== editingAccount?.id)
                        .map(account => (
                          <option key={account.id} value={account.id}>
                            {account.code || ""} - {account.name || ""}
                          </option>
                        ))}
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="account-description"><TranslatedText text="Description (Optional)" /></Label>
                    <Textarea
                      id="account-description"
                      value={accountForm.description || ""}
                      onChange={(e) => setAccountForm({...accountForm, description: e.target.value})}
                      placeholder={tSync("Account description...")}
                    />
                  </div>
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="account-active"
                      checked={!!accountForm.isActive}
                      onCheckedChange={(checked) => setAccountForm({...accountForm, isActive: checked})}
                    />
                    <Label htmlFor="account-active"><TranslatedText text="Account is active" /></Label>
                  </div>
                  <DialogFooter>
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => {
                        // Always allow cancel - don't block UI
                        if (!accountFormLoading) {
                          setShowAccountDialog(false);
                          setEditingAccount(null);
                          setAccountFormError(null);
                          setAccountForm({
                            name: "",
                            code: "",
                            description: "",
                            accountTypeId: undefined,
                            parentId: undefined,
                            isActive: true
                          });
                        }
                      }} 
                      disabled={accountFormLoading}
                    >
                      <TranslatedText text="Cancel" />
                    </Button>
                    <Button type="submit" disabled={accountFormLoading}>
                      {accountFormLoading && <RefreshCw className="mr-2 h-4 w-4 animate-spin" />}
                      {accountFormLoading ? <TranslatedText text="Saving..." /> : (editingAccount ? <TranslatedText text="Update Account" /> : <TranslatedText text="Create Account" />)}
                    </Button>
                  </DialogFooter>
                </form>
              );
            } catch (err) {
              return (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Something went wrong rendering the account form. Please check your form state and try again.
                  </AlertDescription>
                </Alert>
              );
            }
          })()}
        </DialogContent>
      </Dialog>

      {/* Account Type Dialog */}
      <Dialog 
        open={showTypeDialog} 
        onOpenChange={(open) => {
          // Only allow closing if not loading
          if (!open) {
            setShowTypeDialog(false);
            setEditingType(null);
            setTypeFormError(null);
            setTypeForm({
              code: "",
              name: "",
              description: "",
              normalBalance: "debit",
              category: ""
            });
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingType ? <TranslatedText text="Edit Account Type" /> : <TranslatedText text="Add New Account Type" />}
            </DialogTitle>
            <DialogDescription>
              {editingType ? <TranslatedText text="Update account type information" /> : <TranslatedText text="Create a new account type" />}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleTypeSubmit} className="space-y-4">
            {typeFormError && (
              <Alert variant="destructive" className="border-red-500 bg-red-50">
                <AlertCircle className="h-4 w-4 text-red-600" />
                <AlertDescription className="text-red-800 font-medium whitespace-pre-wrap">
                  {typeFormError}
                </AlertDescription>
              </Alert>
            )}
            <div className="space-y-2">
              <Label htmlFor="type-code"><TranslatedText text="Type Code" /> <span className="text-red-500">*</span></Label>
              <Select
                value={typeForm.code || ""}
                onValueChange={(value) => {
                  const selectedType = STANDARD_ACCOUNT_TYPES.find(t => t.code === value);
                  setTypeForm({
                    ...typeForm,
                    code: value,
                    name: selectedType?.name || typeForm.name // Auto-populate name when code is selected
                  });
                  setTypeFormError(null);
                }}
                required
              >
                <SelectTrigger id="type-code" className="w-full">
                  <SelectValue placeholder={tSync("Select account type code")} />
                </SelectTrigger>
                <SelectContent>
                  {STANDARD_ACCOUNT_TYPES.map(type => (
                    <SelectItem key={type.code} value={type.code}>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{type.code}</span>
                        <span className="text-xs text-muted-foreground">({type.name})</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="type-name"><TranslatedText text="Type Name" /> <span className="text-red-500">*</span></Label>
              <Select
                value={typeForm.name || ""}
                onValueChange={(value) => {
                  const selectedType = STANDARD_ACCOUNT_TYPES.find(t => t.name === value);
                  setTypeForm({
                    ...typeForm,
                    name: value,
                    code: selectedType?.code || typeForm.code // Auto-populate code when name is selected
                  });
                  setTypeFormError(null);
                }}
                required
              >
                <SelectTrigger id="type-name" className="w-full">
                  <SelectValue placeholder={tSync("Select account type name")} />
                </SelectTrigger>
                <SelectContent>
                  {STANDARD_ACCOUNT_TYPES.map(type => (
                    <SelectItem key={type.name} value={type.name}>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{type.name}</span>
                        <span className="text-xs text-muted-foreground">({type.code})</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="normal-balance"><TranslatedText text="Normal Balance" /></Label>
              <Select 
                value={typeForm.normalBalance || "debit"}
                onValueChange={(value: "debit" | "credit") => setTypeForm({...typeForm, normalBalance: value})}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="debit"><TranslatedText text="Debit" /></SelectItem>
                  <SelectItem value="credit"><TranslatedText text="Credit" /></SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="type-category"><TranslatedText text="Category (Optional)" /></Label>
              <Input
                id="type-category"
                value={typeForm.category}
                onChange={(e) => setTypeForm({...typeForm, category: e.target.value})}
                placeholder={tSync("e.g., Current Assets")}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="type-description"><TranslatedText text="Description (Optional)" /></Label>
              <Textarea
                id="type-description"
                value={typeForm.description}
                onChange={(e) => setTypeForm({...typeForm, description: e.target.value})}
                placeholder={tSync("Type description...")}
              />
            </div>
            <DialogFooter>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => {
                  setShowTypeDialog(false);
                  setEditingType(null);
                  setTypeFormError(null);
                  setTypeForm({
                    code: "",
                    name: "",
                    description: "",
                    normalBalance: "debit",
                    category: ""
                  });
                }}
              >
                <TranslatedText text="Cancel" />
              </Button>
              <Button type="submit">
                {editingType ? <TranslatedText text="Update Type" /> : <TranslatedText text="Create Type" />}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle><TranslatedText text="Import Accounts" /></DialogTitle>
            <DialogDescription>
              <TranslatedText text="Upload a CSV file to import accounts. The file should include columns for Code, Name, Type, Parent, Description, and Status." />
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
              <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <div>
                <Button variant="outline">
                  <TranslatedText text="Choose File" />
                </Button>
                <p className="text-sm text-muted-foreground mt-2">
                  <TranslatedText text="or drag and drop your CSV file here" />
                </p>
              </div>
            </div>
            
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <TranslatedText text="Make sure your CSV file follows the correct format. Download our template for reference." />
              </AlertDescription>
            </Alert>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowImportDialog(false)}>
              <TranslatedText text="Cancel" />
            </Button>
            <Button variant="outline">
              <Download className="mr-2 h-4 w-4" />
              <TranslatedText text="Download Template" />
            </Button>
            <Button>
              <TranslatedText text="Import Accounts" />
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
